import { formatDate } from '@angular/common';
import { Component, Inject, LOCALE_ID, OnInit } from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';
import { Router } from '@angular/router';
import { AxisLabelClickEvent, SeriesClickEvent } from '@progress/kendo-angular-charts';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { translate } from '@jsverse/transloco';
import { Store, select } from '@ngrx/store';
import { Observable, filter, first, shareReplay } from 'rxjs';
import { IAssignmentType } from 'src/app/assignment/models/assignment-type';
import { AssignmentTypeService } from 'src/app/assignment/services/assignment-type.service';
import { PermissionStorageService } from 'src/app/authentication/storages/permission-storage.service';
import { IEmployee } from 'src/app/employee/employee';
import { IDepartment } from 'src/app/setting/models/departement';
import { IFiscalYear } from 'src/app/setting/models/fiscal-year';
import { DepartementStoreService } from 'src/app/setting/services/departement-store.service';
import { TimeSpan } from 'src/app/shared/helpers/time-span';
import { AppStateInterface } from 'src/app/stores/global/appState.interface';
import { fiscalYearsSelector, getFiscalYears } from 'src/app/stores/global/fiscal-years/fiscal-years.store';
import {
  IEmployeeWorkTimeComparison,
  IEmployeeWorkTimeComparisonResponse
} from '../../models/employee-work-time-comparison';
import {
  IAssignmentTime,
  IAssignmentTimeAnalyticsResponse,
  IAssignmentTypeTime
} from '../../models/assignment-time-analytics';
import {
  ITimeEntryAnalyticsCategory,
  ITimeEntryAnalyticsMonth,
  ITimeEntryAnalyticsResponse
} from '../../models/time-entry-analytics';
import { TimeEntryService } from '../../services/time-entry.service';

/** One column of the chart: hours as numbers for the axis, TimeSpans as text for the tooltip. */
interface IEvaluationDataPoint {
  label: string;
  totalHours: number;
  productiveHours: number;
  unproductiveHours: number;
  uncategorizedHours: number;
  productivity: number;
  totalTime: string;
  productiveTime: string;
  unproductiveTime: string;
  uncategorizedTime: string;
}

/** One bar of the activity chart. The colour encodes productive/unproductive, as in the chart above. */
interface ICategoryDataPoint {
  label: string;
  hours: number;
  time: string;
  share: number;
  color: string;
  productivityLabel: string;
  // Null for the "no activity" bar, which carries no productivity flag.
  isProductive: boolean | null;
}

/** One cell of the activity-by-month heatmap. Kendo derives both axes from the data. */
interface IHeatmapDataPoint {
  activity: string;
  month: string;
  hours: number;
  time: string;
}

/**
 * One stack segment of the time-per-assignment chart: an employee's share of one
 * assignment's bar. Every series carries a point per assignment (zero-filled), so the
 * stacks stay aligned across the category axis.
 */
interface IAssignmentTimePoint {
  assignmentId: string;
  assignment: string;
  employee: string;
  hours: number;
  time: string;
  assignmentTotal: string;
}

/** One chart of the time-per-assignment panel: all assignments of one AssignmentType. */
interface IAssignmentTypeChart {
  label: string;
  totalHours: number;
  height: number;
  // One stacked series per employee, largest share of the type first.
  series: { name: string; data: IAssignmentTimePoint[] }[];
  // Category label to assignment id, so a click on an axis label finds its assignment.
  idsByLabel: { [label: string]: string };
}

/** One entry of the month filter, derived from the selected fiscal year's date range. */
interface IMonthOption {
  year: number;
  month: number;
  label: string;
}

/** One employee row of the target-versus-actual chart: a pair of bars plus the delta. */
interface IWorkTimeDataPoint {
  employee: string;
  targetHours: number;
  actualHours: number;
  targetTime: string;
  actualTime: string;
  differenceTime: string;
  isDeficit: boolean;
}

@UntilDestroy()
@Component({
    selector: 'app-time-evaluation',
    templateUrl: './time-evaluation.component.html',
    styleUrls: ['./time-evaluation.component.css'],
    standalone: false
})
export class TimeEvaluationComponent implements OnInit {

  constructor(
    private timeEntryService: TimeEntryService,
    private assignmentTypeService: AssignmentTypeService,
    private fb: UntypedFormBuilder,
    private store: Store<AppStateInterface>,
    private departementStore: DepartementStoreService,
    private router: Router,
    private permissionStorage: PermissionStorageService,
    @Inject(LOCALE_ID) private locale: string
  ) {
    this.store.dispatch(getFiscalYears());

    if (this.canFilterByDepartment) {
      // The store fetches with pageSize 10 on construction, which would silently cut off
      // the filter once a tenant has more departments than that.
      this.departementStore.listDepartements({ pageNumber: 1, pageSize: 999999 });
    }
  }

  // Only for who sees every employee (Analytics or SearchAll), as on the server: inside a branch
  // of the hierarchy the figures would cover a part of the department and read as the whole.
  canFilterByDepartment = ['Permissions.TimeEntries.Analytics', 'Permissions.TimeEntries.SearchAll']
    .some(permission => (this.permissionStorage.getPermissions() ?? []).includes(permission));

  public fiscalYears$: Observable<IFiscalYear[]> =
    this.store.pipe(select(fiscalYearsSelector));

  public departments$: Observable<IDepartment[]> =
    this.departementStore.departements$;

  // Only the employees whose evaluations the current user may open — the server scopes the
  // figures the same way, so an unreachable pick would only ever return their own numbers.
  public employees$: Observable<IEmployee[]> =
    this.timeEntryService.getVisibleEmployees().pipe(shareReplay({ bufferSize: 1, refCount: false }));

  // A default item without an id means "no filter", so every row is included.
  departmentDefaultItem = { id: null, name: null };
  employeeDefaultItem = { id: null, firstName: null, lastName: null };

  // The fiscal year is mandatory; its default item is only the "nothing chosen yet" state.
  fiscalYearDefaultItem = { id: null, startDate: null, endDate: null };

  /** The months of the selected fiscal year, so the month filter only offers what the charts can show. */
  monthOptions: IMonthOption[] = [];

  assignmentTypes: IAssignmentType[] = [];

  dataPoints: IEvaluationDataPoint[] = [];
  categoryDataPoints: ICategoryDataPoint[] = [];
  heatmapDataPoints: IHeatmapDataPoint[] = [];
  assignmentTypeCharts: IAssignmentTypeChart[] = [];
  workTimeDataPoints: IWorkTimeDataPoint[] = [];
  summary: ITimeEntryAnalyticsResponse | null = null;
  workTimeSummary: IEmployeeWorkTimeComparisonResponse | null = null;

  // All panels start collapsed. A chart built inside a collapsed panel would measure
  // zero width, so the templates only create them once their panel is open.
  productivityExpanded = false;
  activitiesExpanded = false;
  assignmentTimeExpanded = false;
  workTimeExpanded = false;

  seriesColors = {
    productive: '#37b400',
    unproductive: '#ff6358',
    uncategorized: '#a0a0a0'
  };

  /**
   * Base colour of the heatmap, the theme's $primary. Kendo lightens it towards white in
   * proportion to the cell's share of the largest cell, so this is the "many hours" end.
   */
  heatmapColor = '#3c7a93';

  workTimeColors = {
    target: '#a0a0a0',
    actual: '#3c7a93'
  };

  /**
   * The assignment labels double as links to the assignment, so they wear the theme's
   * $primary as a link colour. Kendo offers no cursor option for axis labels, hence the
   * hint text above the charts.
   */
  assignmentAxisLabels = { color: '#3c7a93' };

  // Kendo does not support outsideEnd labels on stacked series, so the productivity is
  // carried by an invisible line series sitting at the height of each stack.
  productivityLabels = {
    visible: true,
    position: 'above',
    background: 'transparent',
    content: (args: { dataItem: IEvaluationDataPoint }) => this.productivityLabel(args.dataItem)
  };

  /** Months without booked time get no label instead of a meaningless "0 %". */
  private productivityLabel(dataPoint: IEvaluationDataPoint): string {
    return dataPoint?.totalHours ? `${Math.round(dataPoint.productivity)} %` : '';
  }

  // The bar chart is not stacked, so Kendo does allow outsideEnd labels here.
  categoryLabels = {
    visible: true,
    position: 'outsideEnd',
    background: 'transparent',
    content: (args: { dataItem: ICategoryDataPoint }) =>
      `${args.dataItem.time} · ${Math.round(args.dataItem.share)} %`
  };

  filterForm = this.fb.group({
    fiscalYear: [null],
    months: [[]],
    department: [null],
    employee: [null],
    assignmentTypeIds: [[]]
  })

  /** The fiscal year preselected on load, restored by the reset button. */
  private defaultFiscalYear: IFiscalYear | null = null;

  get hasFiscalYear(): boolean {
    return !!this.filterForm.value.fiscalYear?.id;
  }

  /** True once a fiscal year is selected but the selection yielded no booked time at all. */
  get isEmpty(): boolean {
    return this.hasFiscalYear && !!this.summary && this.totalHours === 0;
  }

  get totalHours(): number {
    return this.summary ? this.toHours(this.summary.totalTime) : 0;
  }

  /**
   * The active narrowing as one phrase, so both panel titles can state what they show
   * even while collapsed. Null while nothing but the fiscal year is chosen.
   */
  get selectedScope(): string | null {
    const department = this.filterForm.value.department?.id
      ? this.filterForm.value.department.name
      : null;

    const employee = this.filterForm.value.employee?.id
      ? `${this.filterForm.value.employee.firstName} ${this.filterForm.value.employee.lastName}`
      : null;

    const months = (this.filterForm.value.months as IMonthOption[] ?? [])
      .map(month => month.label)
      .join(', ') || null;

    const assignmentTypes = (this.filterForm.value.assignmentTypeIds as string[] ?? [])
      .map(id => this.assignmentTypes.find(type => type.id === id)?.name)
      .filter(Boolean)
      .join(', ') || null;

    return [department, employee, months, assignmentTypes].filter(Boolean).join(', ') || null;
  }

  /** True once the AssignmentType narrowing is active, which the Soll/Ist chart ignores. */
  get hasAssignmentTypeFilter(): boolean {
    return (this.filterForm.value.assignmentTypeIds ?? []).length > 0;
  }

  get selectedFiscalYearLabel(): string {
    return this.fiscalYearLabel(this.filterForm.value.fiscalYear);
  }

  /**
   * Every write path now requires a TimeEntryCategory, so time without one can only come
   * from rows created before that was enforced. Keep the series out of the chart and the
   * legend unless such rows actually turn up in the selected fiscal year.
   */
  get hasUncategorizedTime(): boolean {
    return !!this.summary && this.toHours(this.summary.uncategorizedTime) > 0;
  }

  ngOnInit(): void {
    // The month options belong to the selected fiscal year, so a change of year rebuilds
    // them and drops the old selection, which would name months outside the new year.
    // The control's own valueChanges runs before the form's, so updateChart below already
    // sees the cleared selection; emitEvent stays off to avoid a second request.
    this.filterForm.get('fiscalYear').valueChanges
      .pipe(untilDestroyed(this))
      .subscribe((fiscalYear: IFiscalYear) => {
        this.monthOptions = this.toMonthOptions(fiscalYear);
        this.filterForm.get('months').setValue([], { emitEvent: false });
      });

    this.filterForm.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(() => this.updateChart());

    // Preselect the fiscal year that contains today. Fiscal years load asynchronously on
    // the first visit, so wait for the first non-empty emission instead of reading the
    // store eagerly. On later visits the store replays the cached list synchronously,
    // right inside subscribe - which is why the valueChanges subscriptions above must
    // already be in place, or this patch would never reach updateChart.
    this.fiscalYears$
      .pipe(
        filter(fiscalYears => fiscalYears.length > 0),
        first(),
        untilDestroyed(this)
      )
      .subscribe(fiscalYears => {
        const current = this.findCurrentFiscalYear(fiscalYears);

        if (current) {
          this.defaultFiscalYear = current;
          this.filterForm.get('fiscalYear').patchValue(current);
        }
      });

    this.assignmentTypeService.searchAssignmentTypes({ pageNumber: 0, pageSize: 999999 })
      .pipe(first(), untilDestroyed(this))
      .subscribe(response => {
        this.assignmentTypes = response.data.filter(type => type.isActive);
      });
  }

  /**
   * Back to the state the tab opens in: the current fiscal year and no further narrowing.
   * A plain reset() would clear the mandatory fiscal year and leave the chart empty.
   */
  onResetFilter(): void {
    this.filterForm.reset({
      fiscalYear: this.defaultFiscalYear,
      months: [],
      department: null,
      employee: null,
      assignmentTypeIds: []
    });
  }

  fiscalYearLabel(fiscalYear: IFiscalYear): string {
    if (!fiscalYear?.id) {
      return '';
    }

    const startYear = new Date(fiscalYear.startDate).getFullYear();
    const endYear = new Date(fiscalYear.endDate).getFullYear();

    // A fiscal year may span two calendar years, in which case both belong in the label.
    return startYear === endYear ? `${startYear}` : `${startYear}/${endYear}`;
  }

  /** Walks the fiscal year month by month, mirroring how the backend builds the chart's axis. */
  private toMonthOptions(fiscalYear: IFiscalYear): IMonthOption[] {
    if (!fiscalYear?.id) {
      return [];
    }

    const start = new Date(fiscalYear.startDate);
    const end = new Date(fiscalYear.endDate);
    const options: IMonthOption[] = [];

    for (let cursor = new Date(start.getFullYear(), start.getMonth(), 1);
         cursor <= end;
         cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1)) {
      options.push({
        year: cursor.getFullYear(),
        month: cursor.getMonth() + 1,
        label: formatDate(cursor, 'MMM yy', this.locale)
      });
    }

    return options;
  }

  private findCurrentFiscalYear(fiscalYears: IFiscalYear[]): IFiscalYear | undefined {
    const today = new Date();

    return fiscalYears.find(fiscalYear =>
      new Date(fiscalYear.startDate) <= today && today <= new Date(fiscalYear.endDate));
  }

  private updateChart(){
    const fiscalYear: IFiscalYear = this.filterForm.value.fiscalYear;

    if (!fiscalYear?.id) {
      this.dataPoints = [];
      this.categoryDataPoints = [];
      this.heatmapDataPoints = [];
      this.assignmentTypeCharts = [];
      this.workTimeDataPoints = [];
      this.summary = null;
      this.workTimeSummary = null;
      return;
    }

    const months: IMonthOption[] = this.filterForm.value.months ?? [];
    const assignmentTypeIds: string[] = this.filterForm.value.assignmentTypeIds ?? [];

    const filter = {
      fiscalYearId: fiscalYear.id,
      departmentId: this.filterForm.value.department?.id ?? undefined,
      employeeId: this.filterForm.value.employee?.id ?? undefined,
      months: months.length
        ? months.map(month => ({ year: month.year, month: month.month }))
        : undefined
    };

    // Deliberately without the AssignmentType narrowing: target time carries no assignment,
    // so filtering only the actual side would fake a deficit. The template shows a hint instead.
    this.timeEntryService.getEmployeeWorkTimeComparison(filter)
      .pipe(untilDestroyed(this))
      .subscribe(response => {
        this.workTimeSummary = response;
        this.workTimeDataPoints = response.employees.map(employee => this.toWorkTimeDataPoint(employee));
      });

    this.timeEntryService.getAssignmentTimeAnalytics({
      ...filter,
      assignmentTypeIds: assignmentTypeIds.length ? assignmentTypeIds : undefined
    })
      .pipe(untilDestroyed(this))
      .subscribe(response => {
        this.assignmentTypeCharts = this.toAssignmentTypeCharts(response);
      });

    this.timeEntryService.getTimeEntryAnalytics({
      ...filter,
      assignmentTypeIds: assignmentTypeIds.length ? assignmentTypeIds : undefined
    })
    .pipe(untilDestroyed(this))
    .subscribe(response => {
      this.summary = response;
      this.dataPoints = response.months.map(month => this.toDataPoint(month));

      // Kendo runs a bar chart's category axis downwards, so the backend's largest-first
      // order already puts the longest bar on top.
      this.categoryDataPoints = response.categories.map(category => this.toCategoryDataPoint(category));
      this.heatmapDataPoints = this.toHeatmapDataPoints(response);
    })
  }

  /**
   * A full activity-by-month grid. Kendo reads both axes off the data in the order the
   * points arrive, so activities have to come outermost in their ranked order and months
   * innermost in calendar order. The backend only sends months a category was booked in,
   * so the gaps are filled with zeros here, which the colour scale renders as blank cells.
   */
  private toHeatmapDataPoints(response: ITimeEntryAnalyticsResponse): IHeatmapDataPoint[] {
    const points: IHeatmapDataPoint[] = [];

    for (const category of response.categories) {
      const activity = this.categoryLabel(category);

      for (const month of response.months) {
        const booked = month.categories.find(entry => entry.categoryId === category.categoryId);

        points.push({
          activity,
          month: this.monthLabel(month),
          hours: booked ? this.toHours(booked.totalTime) : 0,
          time: booked ? this.toHoursAndMinutes(booked.totalTime) : this.toHoursAndMinutes('00:00:00')
        });
      }
    }

    return points;
  }

  /** Grows with the number of activities, like the bar chart above it. */
  get heatmapChartHeight(): number {
    return Math.max(160, this.categoryDataPoints.length * 36 + 90);
  }

  /** One chart per AssignmentType: the type's assignments as bars, stacked by employee. */
  private toAssignmentTypeCharts(response: IAssignmentTimeAnalyticsResponse): IAssignmentTypeChart[] {
    return response.types.map(type => this.toAssignmentTypeChart(type));
  }

  private toAssignmentTypeChart(type: IAssignmentTypeTime): IAssignmentTypeChart {
    // Employees ranked by their share of the whole type, so the biggest segments lead
    // the stacks and the legend.
    const employees = new Map<string, { name: string; hours: number }>();

    for (const assignment of type.assignments) {
      for (const employee of assignment.employees) {
        const ranked = employees.get(employee.employeeId)
          ?? { name: `${employee.firstName} ${employee.lastName}`, hours: 0 };

        ranked.hours += this.toHours(employee.totalTime);
        employees.set(employee.employeeId, ranked);
      }
    }

    const series = [...employees.entries()]
      .sort(([, a], [, b]) => b.hours - a.hours)
      .map(([employeeId, employee]) => ({
        name: employee.name,
        data: type.assignments.map(assignment =>
          this.toAssignmentTimePoint(assignment, employeeId, employee.name))
      }));

    const idsByLabel: { [label: string]: string } = {};

    for (const assignment of type.assignments) {
      idsByLabel[this.assignmentLabel(assignment)] = assignment.assignmentId;
    }

    return {
      label: type.typeName ?? translate('time-entries.evaluation.withoutAssignmentType'),
      totalHours: this.toHours(type.totalTime),
      height: Math.max(200, type.assignments.length * 44 + 100),
      series,
      idsByLabel
    };
  }

  /** A click on a stack segment opens the assignment the bar belongs to. */
  onAssignmentSeriesClick(event: SeriesClickEvent): void {
    this.openAssignment(event.dataItem?.assignmentId);
  }

  /** A click on an assignment label opens it too. Value-axis labels miss the lookup. */
  onAssignmentAxisLabelClick(event: AxisLabelClickEvent, chart: IAssignmentTypeChart): void {
    this.openAssignment(chart.idsByLabel[event.value]);
  }

  private openAssignment(assignmentId: string | undefined): void {
    if (assignmentId) {
      this.router.navigate(['/assignment', assignmentId]);
    }
  }

  /**
   * An employee's share of one assignment's bar. Kendo aligns stacked series by category,
   * so every series carries a point per assignment, zero-filled where nothing was booked.
   */
  private toAssignmentTimePoint(
    assignment: IAssignmentTime, employeeId: string, employeeName: string): IAssignmentTimePoint {
    const booked = assignment.employees.find(employee => employee.employeeId === employeeId);

    return {
      assignmentId: assignment.assignmentId,
      assignment: this.assignmentLabel(assignment),
      employee: employeeName,
      hours: booked ? this.toHours(booked.totalTime) : 0,
      time: this.toHoursAndMinutes(booked?.totalTime ?? '00:00:00'),
      assignmentTotal: this.toHoursAndMinutes(assignment.totalTime)
    };
  }

  /** Same label as the assignment picker: the title, plus the internal number when there is one. */
  private assignmentLabel(assignment: IAssignmentTime): string {
    return assignment.internalNumber
      ? `${assignment.title} | ${assignment.internalNumber}`
      : assignment.title;
  }

  private toWorkTimeDataPoint(employee: IEmployeeWorkTimeComparison): IWorkTimeDataPoint {
    return {
      employee: `${employee.firstName} ${employee.lastName}`,
      targetHours: this.toHours(employee.targetTime),
      actualHours: this.toHours(employee.actualTime),
      targetTime: this.toHoursAndMinutes(employee.targetTime),
      actualTime: this.toHoursAndMinutes(employee.actualTime),
      differenceTime: this.toHoursAndMinutes(employee.difference),
      isDeficit: this.toHours(employee.difference) < 0
    };
  }

  /** Two bars per employee, so this grows faster than the single-bar charts. */
  get workTimeChartHeight(): number {
    return Math.max(200, this.workTimeDataPoints.length * 56 + 80);
  }

  get workTimeTotalHours(): number {
    return this.workTimeSummary ? this.toHours(this.workTimeSummary.targetTime) : 0;
  }

  get workTimeDifference(): string {
    return this.workTimeSummary ? this.toHoursAndMinutes(this.workTimeSummary.difference) : '';
  }

  get hasWorkTimeDeficit(): boolean {
    return !!this.workTimeSummary && this.toHours(this.workTimeSummary.difference) < 0;
  }

  /** True once a fiscal year is selected but no employee owed or booked anything. */
  get isWorkTimeEmpty(): boolean {
    return !!this.workTimeSummary && this.workTimeDataPoints.length === 0;
  }

  /** The day the target time stops accruing: today, or the fiscal year's end if it is over. */
  get workTimeEndDate(): Date | null {
    return this.workTimeSummary ? new Date(this.workTimeSummary.endDate) : null;
  }

  /** A fiscal year may span two calendar years, so the year belongs in the label. */
  private monthLabel(month: ITimeEntryAnalyticsMonth): string {
    return formatDate(new Date(month.year, month.month - 1, 1), 'MMM yy', this.locale);
  }

  private categoryLabel(category: ITimeEntryAnalyticsCategory): string {
    return category.name ?? translate('time-entries.evaluation.uncategorized');
  }

  private toDataPoint(month: ITimeEntryAnalyticsMonth): IEvaluationDataPoint {
    return {
      label: this.monthLabel(month),
      totalHours: this.toHours(month.totalTime),
      productiveHours: this.toHours(month.productiveTime),
      unproductiveHours: this.toHours(month.unproductiveTime),
      uncategorizedHours: this.toHours(month.uncategorizedTime),
      productivity: month.productivity,
      totalTime: this.toHoursAndMinutes(month.totalTime),
      productiveTime: this.toHoursAndMinutes(month.productiveTime),
      unproductiveTime: this.toHoursAndMinutes(month.unproductiveTime),
      uncategorizedTime: this.toHoursAndMinutes(month.uncategorizedTime)
    };
  }

  private toCategoryDataPoint(category: ITimeEntryAnalyticsCategory): ICategoryDataPoint {
    return {
      label: this.categoryLabel(category),
      hours: this.toHours(category.totalTime),
      time: this.toHoursAndMinutes(category.totalTime),
      share: category.share,
      color: this.categoryColor(category),
      productivityLabel: this.categoryProductivityLabel(category),
      isProductive: category.isProductive
    };
  }

  /**
   * The unproductive activity that swallowed the most time, or null when nothing
   * unproductive was booked. The list is largest-first, so the first hit is the biggest.
   */
  get topUnproductiveCategory(): ICategoryDataPoint | null {
    return this.categoryDataPoints.find(point => point.isProductive === false) ?? null;
  }

  /** Same colour coding as the monthly chart, so both read as one picture. */
  private categoryColor(category: ITimeEntryAnalyticsCategory): string {
    if (category.isProductive === null) {
      return this.seriesColors.uncategorized;
    }

    return category.isProductive
      ? this.seriesColors.productive
      : this.seriesColors.unproductive;
  }

  private categoryProductivityLabel(category: ITimeEntryAnalyticsCategory): string {
    if (category.isProductive === null) {
      return translate('time-entries.evaluation.uncategorized');
    }

    return category.isProductive
      ? translate('time-entries.evaluation.productive')
      : translate('time-entries.evaluation.unproductive');
  }

  /** The bar chart grows with the number of activities instead of squeezing them together. */
  get categoryChartHeight(): number {
    return Math.max(160, this.categoryDataPoints.length * 44 + 60);
  }

  /** Decimal hours, rounded to two places, for the value axis. */
  private toHours(time: string): number {
    return Math.round(TimeSpan.parse(time).getTotalSeconds() / 36) / 100;
  }

  /** "h:mm" for display, matching how the report grids render durations. */
  private toHoursAndMinutes(time: string): string {
    return TimeSpan.parse(time).toStringShort();
  }
}
